<template>
    <!-- Search Panel -->
    <section id="search-panels" class="searchPanel search-panels" :style="{ backgroundImage: `url(/images/hero/${tabImage})` }">
        <div class="searchPanel-cover" />
        <div class="panels" ref="panels">
            <div class="container">
                <!--  qs-router linkes -->
                <ul class="searchPanel-segment">
                    <li class="col-xs-4 col-md-2" v-for="tab in tabs" :key="tab.key">
                        <nuxt-link
                            :to="tab.link"
                            :class="{'router-link-active' : activeLink === tab.key}"
                            :data-test="tab.key + '-link'"
                            no-prefetch
                        >
                            <div class="svg-icon-wrapper">
                                <component :is="tab.icon" />
                            </div>
                            <p class="tabs-title mb-1">
                                {{ tab.title }}
                            </p>
                        </nuxt-link>
                    </li>
                </ul>
                <keep-alive>
                    <component :disabled="loading" :is="(activeLink || 'domestic') + '-search'" @create-new-search="createSearch(activeLink, $event)" @error="openAlert" />
                </keep-alive>
            </div>
        </div>
    </section>
</template>

<script>
import { mapMutations, mapActions, mapState, mapGetters } from 'vuex';
import DomesticSearch from '~/components/pages/domestic/search/domestic-search';
import InternationalSearch from '~/components/pages/international/search/international-search';
import TrainSearch from '~/components/pages/train/search/train-search';
import BusSearch from '~/components/pages/bus/search/bus-search';
import HotelSearch from '~/components/pages/international-hotel/search/hotel-search';
import TourSearch from '~/components/pages/tour/search/tour-search';
import IconDomesticFlightCircle from '~icons/domestic-flight-circle';
import IconInternationalFlightCircle from '~icons/international-flight-circle';
import IconHotelCircle from '~/components/icons/international-hotels-circle';
import IconTrainCircle from '~icons/train-circle';
import IconBusCircle from '~icons/bus/bus-circle';
import IconTourCircle from '~icons/tour/tour-circle';
import urlHelper from '~/scripts/mixins/url-helper';

export default {
    components: {
        DomesticSearch,
        InternationalSearch,
        TrainSearch,
        BusSearch,
        HotelSearch,
        TourSearch,
        IconDomesticFlightCircle,
        IconInternationalFlightCircle,
        IconHotelCircle,
        IconTrainCircle,
        IconBusCircle,
        IconTourCircle,
    },
    mixins: [urlHelper],
    props: {
        activeLink: {
            type: String,
            default: '',
        },
    },
    data () {
        return {
            loading: false,
        };
    },
    computed: {
        ...mapState('international', ['multiDestinationTrips']),
        ...mapGetters('international-hotel', ['minimalState']),
        ...mapState('international-hotel', ['baseUrl']),
        tabs () {
            return [
                {
                    key: 'domestic',
                    link: '/',
                    title: this.$t('common.domesticFlight'),
                    icon: 'icon-domestic-flight-circle',
                },
                {
                    key: 'international',
                    link: '/iranout',
                    title: this.$t('common.internationalFlight'),
                    icon: 'icon-international-flight-circle',
                },
                {
                    key: 'train',
                    link: '/train-ticket',
                    title: this.$t('common.train'),
                    icon: 'icon-train-circle',
                },
                {
                    key: 'bus',
                    link: '/bus-ticket',
                    title: this.$t('common.bus'),
                    icon: 'icon-bus-circle',
                },
                {
                    key: 'tour',
                    link: '/tour',
                    title: this.$t('common.tour'),
                    icon: 'icon-tour-circle',
                },
                {
                    key: 'hotel',
                    link: '/hotel',
                    title: this.$device.mobile ? this.$t('common.hotel') : this.$t('common.hotelDesktop'),
                    icon: 'icon-hotel-circle',
                },
            ];
        },
        tabImage () {
            switch (this.activeLink) {
            case 'train':
                return 'train-bg.jpg';
            case 'bus':
                return 'bus-bg.jpg';
            case 'hotel':
                return 'hotel-bg.jpg';
            case 'tour':
                return 'tour-bg.jpg';
            default:
                return 'flight-bg.jpg';
            }
            // return this.$route.query.tab === 'train' ? 'alibaba-train/bg.jpg' : 'new-hero-image.jpg';
        },
    },
    activeClass () {
        return 'router-link-active';
    },
    created () {
        this.trainResetSearchDataToDefault();
        this.domesticResetSearchDataToDefault();
        this.internationalResetSearchDataToDefault();
        this.busResetSearchDataToDefault();
        this.internationalHotelsResetSearchDataToDefault();
    },
    methods: {
        ...mapActions(['openAlert']),
        ...mapMutations('international', {
            internationalSetResults: 'setResults',
            internationalResetSearchDataToDefault: 'resetSearchDataToDefault',
        }),
        ...mapMutations('domestic', {
            domesticSetResults: 'setResults',
            domesticResetSearchDataToDefault: 'resetSearchDataToDefault',
        }),
        ...mapMutations('train', {
            trainSetResults: 'setResults',
            trainResetSearchDataToDefault: 'resetSearchDataToDefault',
        }),
        ...mapMutations('bus', {
            busSetResults: 'setResults',
            busResetSearchDataToDefault: 'resetSearchDataToDefault',
        }),
        ...mapMutations(
            'international-hotel', {
                internationalHotelsSetResults: 'setResults',
                internationalHotelsResetSearchDataToDefault: 'resetSearchDataToDefault',
            }
        ),
        createSearch (searchType, data = {}) {
            this.loading = true;
            this[searchType + 'Search'](data);
        },

        domesticSearch () {
            this.redirectToURI({
                ...this.getDataForQueryFromState(this.$store.state.domestic),
                step: 'results',
                path: '/flights/',
            });
        },
        internationalSearch () {
            const state = this.$store.state.international;
            const trips = this.multiDestinationTrips;
            // if request type is oneway or roundtrip then call createSearch
            const isRoundTrip = trips.length === 1 || (trips.length === 2 && trips[0].origin.domainCode === trips[1].destination.domainCode && trips[0].destination.domainCode === trips[1].origin.domainCode);
            if (state.type === 'multiCity' && !isRoundTrip) {
                this.redirectToMultiDestinationURI({
                    ...this.getDataForMultiDestinationQueryFromState(state),
                    step: 'results',
                    path: '/international/',
                });
            } else {
                this.redirectToURI({
                    ...this.getDataForQueryFromState(state),
                    step: 'results',
                    path: '/international/',
                });
            }
        },
        trainSearch () {
            this.redirectToURI({
                ...this.getDataForQueryFromState(this.$store.state.train),
                step: 'results',
                path: '/train/',
                ...(this.$route.query.market ? {
                    availableTargetType: this.$route.query.market,
                } : {}),
            });
        },
        busSearch () {
            this.redirectToURI({
                ...this.getDataForQueryFromState(this.$store.state.bus),
                step: 'results',
                path: '/bus/',
            });
        },
        hotelSearch () {
            const state = this.$store.state['international-hotel'];
            this.$router.push({
                path: state.destination.type === 'City' ? `${this.baseUrl}/search/results` : `${this.baseUrl}/${state.destination.link}`,
                query: Object.assign({}, this.$route.query, this.minimalState),
            });
        },
        tourSearch ({ destination, ...query }) {
            // TODO m.hooshdar: check later
            this.$store.commit('tour/resetKey', { key: 'availablePackages' });
            this.$router.push({ path: '/tour/' + destination, query });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~vars';

.searchPanel {

    &-segment {
        margin: -25px 0 45px;
        @media (min-width: 768px) {
            margin: -38px 0 40px;
        }
        @media (min-width: 992px) {
            margin: -30px 0 10px;
        }
    }

    /deep/ .gap {
        @media (max-width: 991px) {

            .radifier + .radifier-label::before {
                background-color: $snow-white;
            }
        }

        @media (min-width: 992px) {

            .radifier + .radifier-label {
                color: $snow-white !important;
            }
        }
    }
}

.panels {
    min-height: 220px;
    padding-bottom: 10px;
    @media (min-width: 992px) {
        background: rgba($black, 0.7);
    }
}
</style>
